<script lang="ts">
    import { pendingConfirm, answerConfirm } from '$lib/stores/confirm';

    /**
     * The confirmation prompt for {@link confirmAction}. Mounted once in the
     * root layout so every route shares it.
     */

    let confirmButton = $state<HTMLButtonElement | undefined>();

    // Put the keyboard on the confirm button as the dialog opens, so Enter
    // answers it and Tab stays inside the two choices.
    $effect(() => {
        if ($pendingConfirm && confirmButton) {
            confirmButton.focus();
        }
    });

    function onKeydown(event: KeyboardEvent) {
        if (event.key === 'Escape') {
            event.stopPropagation();
            answerConfirm(false);
        }
    }
</script>

{#if $pendingConfirm}
    <!-- svelte-ignore a11y_no_noninteractive_element_interactions -->
    <div
        class="dialog-overlay confirm-overlay"
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-title"
        aria-describedby="confirm-message"
        tabindex="-1"
        onkeydown={onKeydown}
    >
        <div class="dialog-container confirm-container">
            <div class="dialog-header">
                <h2 id="confirm-title">{$pendingConfirm.title}</h2>
                <button
                    class="dialog-close"
                    aria-label="Cancel"
                    onclick={() => answerConfirm(false)}
                >
                    ×
                </button>
            </div>
            <div class="dialog-content">
                <p id="confirm-message" class="confirm-message">{$pendingConfirm.message}</p>
            </div>
            <div class="dialog-footer">
                <button class="dialog-button cancel" onclick={() => answerConfirm(false)}>
                    {$pendingConfirm.cancelLabel}
                </button>
                <button
                    bind:this={confirmButton}
                    class="dialog-button confirm {$pendingConfirm.danger ? 'danger' : ''}"
                    onclick={() => answerConfirm(true)}
                >
                    {$pendingConfirm.confirmLabel}
                </button>
            </div>
        </div>
    </div>
{/if}

<style>
    /* The dialog classes are repeated here rather than taken from shared.css,
       because this component is mounted by the root layout and must look the
       same on a route that does not import those styles. */
    .confirm-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-color: rgba(0, 0, 0, 0.5);
        display: flex;
        justify-content: center;
        align-items: center;
        /* Above every other dialog: a confirmation can be raised from one. */
        z-index: 2000;
    }

    .confirm-container {
        background-color: white;
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        width: 460px;
        max-width: 90%;
        max-height: 90vh;
        display: flex;
        flex-direction: column;
    }

    .confirm-container .dialog-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 16px 20px;
        border-bottom: 1px solid #eee;
    }

    .confirm-container .dialog-header h2 {
        margin: 0;
        font-size: 1.2em;
        color: #333;
    }

    .confirm-container .dialog-close {
        background: none;
        border: none;
        font-size: 1.5em;
        cursor: pointer;
        color: #666;
        padding: 0;
        margin: 0;
        width: 30px;
        height: 30px;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .confirm-container .dialog-close:hover {
        color: #333;
    }

    .confirm-container .dialog-content {
        padding: 20px;
        overflow-y: auto;
    }

    .confirm-message {
        margin: 0;
        color: #333;
        line-height: 1.5;
        /* The longer prompts are written as several lines. */
        white-space: pre-line;
    }

    .confirm-container .dialog-footer {
        display: flex;
        justify-content: flex-end;
        padding: 16px 20px;
        border-top: 1px solid #eee;
        gap: 10px;
    }

    .confirm-container .dialog-button {
        padding: 8px 16px;
        border-radius: 4px;
        font-weight: 500;
        font-size: 1em;
        font-family: inherit;
        cursor: pointer;
    }

    .confirm-container .dialog-button.cancel {
        background-color: #f5f5f5;
        color: #333;
        border: 1px solid #ddd;
    }

    .confirm-container .dialog-button.cancel:hover {
        background-color: #e8e8e8;
    }

    .confirm-container .dialog-button.confirm {
        background-color: #4a6fa5;
        color: white;
        border: none;
    }

    .confirm-container .dialog-button.confirm:hover {
        background-color: #3d5c8a;
    }

    .confirm-container .dialog-button.confirm.danger {
        background-color: #c62828;
    }

    .confirm-container .dialog-button.confirm.danger:hover {
        background-color: #a91f1f;
    }

    .confirm-container .dialog-button:focus-visible {
        outline: 2px solid #4a6fa5;
        outline-offset: 2px;
    }
</style>
